import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import styled from 'styled-components';
import Logo from './Logo';
import { useTranslation } from 'react-i18next';

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => props.isOpen ? '16rem' : '4rem'}; /* Adjust width based on isOpen */
  background: linear-gradient(to bottom, #1a1a1a, #2a2a2a, #3a3a3a); /* Darker gradient */
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  transition: width 0.3s ease;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
  @media (max-width: 767px) {
    width: ${props => props.isOpen ? 'min(260px, 82vw)' : '0'};
    overflow: hidden;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem 0.75rem;
  width: 100%;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  
  span {
    margin-top: 0.1rem; /* Reducido de 0.25rem a 0.1rem para acercar mucho mÃ¡s al logo */
    font-size: 1rem;
    font-weight: 900; /* Orbitron Black weight */
    font-family: 'Orbitron', monospace; /* Aplicar fuente Orbitron como el tÃ­tulo principal */
    color: rgba(255, 255, 255, 0.95); /* Mismo color que el tÃ­tulo principal */
    letter-spacing: 0.5px; /* Espaciado entre letras */
    opacity: ${props => props.isOpen ? 1 : 0};
    transition: opacity 0.3s ease;
    white-space: nowrap;
    text-align: center;
  }
`;

const ADMIN_EMAILS = new Set(['edwinpinto103@hotmail.com', 'edwinpinto103author@gmail.com']);

const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { user } = useUser();
  const userPlan = user?.plan || null;
  const { t } = useTranslation();
  const isAdmin = ADMIN_EMAILS.has(user?.email || '');

  const menuItems = [
    { path: '/', icon: '\u2302', iconColor: '#22d3ee', i18nKey: 'sidebar.dashboard' },
    { path: '/chat', icon: '+', iconColor: '#38bdf8', i18nKey: 'sidebar.new_chat' },
    { path: '/codeproject-ai', icon: '\u25A3', iconColor: '#facc15', i18nKey: 'sidebar.new_project', isProject: true },
    { path: '/history', icon: '\u2630', iconColor: '#34d399', i18nKey: 'sidebar.history' },
    { path: '/tools', icon: '\u2692', iconColor: '#fb923c', i18nKey: 'sidebar.tools' },
    { path: '/my-gpts', icon: '\u25C9', iconColor: '#a78bfa', i18nKey: 'sidebar.my_gpts' },
    { path: '/plans', icon: '\u25C6', iconColor: '#22c55e', i18nKey: 'sidebar.plans' },
    { path: '/billing', icon: '\u25A4', iconColor: '#e5e7eb', label: 'Facturación', i18nKey: 'sidebar.billing' },
    { path: '/support', icon: '\u25A1', iconColor: '#f8fafc', label: 'Soporte', i18nKey: 'sidebar.support' },
    { path: '/user', icon: '\u25CF', iconColor: '#cbd5e1', i18nKey: 'sidebar.my_profile' },
    ...(isAdmin ? [{ path: '/admin', icon: '\u2699', iconColor: '#94a3b8', label: 'Admin' }] : [])
  ];

  return (
    <>
      {/* Overlay para mÃ³vil */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      <SidebarContainer isOpen={isOpen}>
        <SidebarHeader isOpen={isOpen}>
          <Logo sidebar className="min-w-[48px]" />
          {isOpen && <span>IntelliVerseOne</span>}
        </SidebarHeader>
        {/* MenÃº de navegaciÃ³n minimalista */}
        <nav className="flex-1 w-full flex flex-col items-center mt-4 space-y-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const itemLabel = item.i18nKey ? t(item.i18nKey, item.label) : item.label;
            // NavegaciÃ³n consistente: permitir entrar a la pÃ¡gina siempre.
            // El gating para acciones se maneja dentro de cada secciÃ³n con useRequireAuthAction.
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 w-11/12 px-3 py-2 rounded-lg transition-all duration-200 font-medium text-base hover:bg-primary/10 hover:text-primary ${
                  isActive ? 'bg-primary/20 text-primary font-bold shadow' : 'text-gray-300'
                }`}
                title={itemLabel}
              >
                <span className="text-xl" style={{ color: item.iconColor }}>{item.icon}</span>
                {isOpen && <span className="truncate">{itemLabel}</span>}
              </Link>
            );
          })}
        </nav>
        {/* Footer compacto con plan actual - TamaÃ±o fijo para evitar cambios */}
        <div className="w-full py-4 border-t border-gray-800 flex flex-col items-center bg-gradient-to-r from-gray-800/30 via-gray-700/20 to-gray-800/30">
          <span className="text-sm text-gray-400 mb-2">{t('user.plan.current')}</span>
          {/* Contenedor con altura fija para evitar cambios de tamaÃ±o */}
          <div className="flex flex-col items-center w-full min-h-[120px] justify-start">
            {userPlan ? (
            <div className={`flex items-center gap-2 text-base font-bold capitalize h-[48px] px-4 py-3 rounded-lg mb-2 ${
              userPlan === 'trial' ? 'text-gray-300 bg-gray-800/50 border border-gray-600' :
              userPlan === 'pro' ? 'text-warning bg-yellow-900/20 border border-yellow-600/30' :
              'text-success bg-green-900/20 border border-green-600/30'
            }`}>
              <span>{
                userPlan === 'trial' ? '\u25CF' :
                userPlan === 'pro' ? '\u25CF' :
                '\u25C6'
              }</span>
              <span>{
                userPlan === 'pro' ? t('user.plan.pro') :
                userPlan === 'pro_plus' ? t('user.plan.pro_plus') :
                t('user.plan.trial')
              }</span>
            </div>
            ) : (
            <div className="flex items-center gap-2 text-base font-bold h-[48px] px-4 py-3 rounded-lg mb-2 text-gray-500 bg-gray-800/30 border border-gray-700 animate-pulse">
              <span>...</span>
            </div>
            )}
            
            {/* Contenedor para informaciÃ³n adicional con altura fija */}
            <div className="flex flex-col items-center min-h-[60px] justify-start">
              {userPlan === 'trial' && (user?.trialDaysLeft || 0) > 0 && (
                <span className="text-sm text-gray-500 mb-2">{t('sidebar.days_left', { count: user?.trialDaysLeft || 0 })}</span>
              )}
              {userPlan === 'trial' && (
                <Link 
                  to="/plans"
                  className="text-sm bg-primary hover:bg-primary-dark px-4 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-primary/25"
                >
                  {t('user.plan.upgrade')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
